import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GetBeacon4PerformanceNetworkOverviewDashboard, GetBeacon4PerformanceNetworkOverviewTable, GetBeacon4PerformanceNetworkOverviewAvailableKpis } from '../requests/Beacon4PerformanceNetworkOverviewRequests';

export const loadBeacon4PerformanceNetworkOverviewDashboard = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewKpiData/loadDashboard',
	async (params, { rejectWithValue }) => {
		try {
			const res = await GetBeacon4PerformanceNetworkOverviewDashboard(params);
			return {
				tech: params.tech,
				mainKpiKey: params.mainKpiKey,
				data: res?.data ?? res,
			};
		} catch (e) {
			return rejectWithValue(e.response?.data ?? e.message);
		}
	}
);

// Lookup of available techs + main KPIs per tech (drives the FilterBar dropdowns)
export const loadBeacon4PerformanceNetworkOverviewAvailableKpis = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewKpiData/loadAvailableKpis',
	async (_, { rejectWithValue }) => {
		try {
			const res = await GetBeacon4PerformanceNetworkOverviewAvailableKpis();
			return res?.data ?? res;
		} catch (e) {
			return rejectWithValue(e.response?.data ?? e.message);
		}
	}
);

// Aggregation Mode table (left panel KPI broken down by spatial dimension over time)
export const loadBeacon4PerformanceNetworkOverviewTable = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewKpiData/loadTable',
	async (params, { rejectWithValue }) => {
		try {
			const res = await GetBeacon4PerformanceNetworkOverviewTable(params);
			return res?.data ?? res;
		} catch (e) {
			return rejectWithValue(e.response?.data ?? e.message);
		}
	}
);

const beacon4PerformanceNetworkOverviewKpiDataSlice = createSlice({
	name: 'beacon4PerformanceNetworkOverviewKpiData',
	initialState: {
		// { [tech]: { [mainKpiKey]: { leftPanel, rightPanel } } }
		dashboardByTech: {},
		table:        null,    // last-loaded aggregation table
		tableLoading: false,
		availableTechs: [],
		availableKpisByTech: {},
		availableLoading: false,
		loading:      false,
		error:        null,
	},
	reducers: {
		clearBeacon4PerformanceNetworkOverviewDashboard(state) {
			state.dashboardByTech = {};
			state.table   = null;
			state.loading = false;
			state.error   = null;
		},
	},
	extraReducers: (b) => {
		b.addCase(loadBeacon4PerformanceNetworkOverviewDashboard.pending, (s) => { s.loading = true; s.error = null; })
		 .addCase(loadBeacon4PerformanceNetworkOverviewDashboard.fulfilled, (s, a) => {
			s.loading = false;
			const { tech, mainKpiKey, data } = a.payload;
			if (!s.dashboardByTech[tech]) s.dashboardByTech[tech] = {};
			s.dashboardByTech[tech][mainKpiKey] = data;
		 })
		 .addCase(loadBeacon4PerformanceNetworkOverviewDashboard.rejected, (s, a) => { s.loading = false; s.error = a.payload; })

		 .addCase(loadBeacon4PerformanceNetworkOverviewTable.pending,   (s) => { s.tableLoading = true; })
		 .addCase(loadBeacon4PerformanceNetworkOverviewTable.fulfilled, (s, a) => { s.tableLoading = false; s.table = a.payload; })
		 .addCase(loadBeacon4PerformanceNetworkOverviewTable.rejected,  (s, a) => { s.tableLoading = false; s.error = a.payload; })

		 .addCase(loadBeacon4PerformanceNetworkOverviewAvailableKpis.pending,   (s) => { s.availableLoading = true; })
		 .addCase(loadBeacon4PerformanceNetworkOverviewAvailableKpis.fulfilled, (s, a) => {
			s.availableLoading = false;
			s.availableTechs       = a.payload?.techs ?? [];
			s.availableKpisByTech  = a.payload?.kpisByTech ?? {};
		 })
		 .addCase(loadBeacon4PerformanceNetworkOverviewAvailableKpis.rejected,  (s, a) => { s.availableLoading = false; s.error = a.payload; });
	},
});

export const { clearBeacon4PerformanceNetworkOverviewDashboard } = beacon4PerformanceNetworkOverviewKpiDataSlice.actions;
export default beacon4PerformanceNetworkOverviewKpiDataSlice.reducer;

export const selectBeacon4PerformanceNetworkOverviewDashboardByTech = (state) => state.beacon4PerformanceNetworkOverviewKpiData.dashboardByTech;
export const selectBeacon4PerformanceNetworkOverviewLoading         = (state) => state.beacon4PerformanceNetworkOverviewKpiData.loading;
export const selectBeacon4PerformanceNetworkOverviewTable           = (state) => state.beacon4PerformanceNetworkOverviewKpiData.table;
export const selectBeacon4PerformanceNetworkOverviewTableLoading    = (state) => state.beacon4PerformanceNetworkOverviewKpiData.tableLoading;
export const selectBeacon4PerformanceNetworkOverviewAvailableTechs      = (state) => state.beacon4PerformanceNetworkOverviewKpiData.availableTechs;
export const selectBeacon4PerformanceNetworkOverviewAvailableKpisByTech = (state) => state.beacon4PerformanceNetworkOverviewKpiData.availableKpisByTech;
export const selectBeacon4PerformanceNetworkOverviewAvailableLoading    = (state) => state.beacon4PerformanceNetworkOverviewKpiData.availableLoading;
